import React, { useState } from 'react';
import { Button } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import useDebounce from 'utils/hooks/useDebounce';
import InstagramAudioSearchBar from './instagramAudioPickerCard/InstagramAudioSearchBar';
import InstagramAudioTypeTabs from './instagramAudioPickerCard/InstagramAudioTypeTabs';
import InstagramAudioTrackList from './instagramAudioPickerCard/InstagramAudioTrackList';
import InstagramAudioTrackListEmpty from './instagramAudioPickerCard/InstagramAudioTrackListEmpty';
import InstagramAudioTrackListError from './instagramAudioPickerCard/InstagramAudioTrackListError';
import InstagramAudioTrackListSkeleton from './instagramAudioPickerCard/InstagramAudioTrackListSkeleton';
import { useInstagramAudioTracksQuery } from '../hooks/useInstagramAudioTracksQuery';
import { useStopAudioPreviewOnUnmount } from '../hooks/useAudioPreview';
import { getInstagramAudioErrorCode } from '../utils/instagramAudioError';
import { DEFAULT_AUDIO_TYPE_FILTER } from '../constants';
import type { InstagramAudioTrack, InstagramAudioTypeFilter } from '../types';

const SEARCH_DEBOUNCE_MS = 500;

interface InstagramAudioPickerCardProps {
  pageId: number;
  selectedAudioId: string | null;
  canPromptConnection: boolean;
  onBack: () => void;
  onSelect: (track: InstagramAudioTrack) => void;
  onRemove: () => void;
}

/**
 * `pendingTrack` is deliberately tri-state: `undefined` is untouched and falls
 * back to the committed selection, `null` is a staged removal, and a track is
 * a staged pick. Nothing is committed until Save.
 */
const InstagramAudioPickerCard = ({
  pageId,
  selectedAudioId,
  canPromptConnection,
  onBack,
  onSelect,
  onRemove,
}: InstagramAudioPickerCardProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [audioType, setAudioType] = useState<InstagramAudioTypeFilter>(
    DEFAULT_AUDIO_TYPE_FILTER,
  );

  const [pendingTrack, setPendingTrack] = useState<
    InstagramAudioTrack | null | undefined
  >(undefined);

  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
  const isSearchPending = searchQuery !== debouncedSearchQuery;

  useStopAudioPreviewOnUnmount();

  const effectiveSelectedId =
    pendingTrack === undefined
      ? selectedAudioId
      : pendingTrack?.audio_id ?? null;

  const tracksQuery = useInstagramAudioTracksQuery({
    pageId,
    audioType,
    searchQuery: debouncedSearchQuery,
  });

  const handleConfirm = () => {
    if (pendingTrack !== undefined) {
      if (pendingTrack === null) {
        onRemove();
      } else {
        onSelect(pendingTrack);
      }
    }

    onBack();
  };

  function renderTracks() {
    if (isSearchPending || tracksQuery.isLoading) {
      return <InstagramAudioTrackListSkeleton />;
    }

    if (tracksQuery.isError) {
      return (
        <InstagramAudioTrackListError
          pageId={pageId}
          errorCode={getInstagramAudioErrorCode(tracksQuery.error)}
          canPromptConnection={canPromptConnection}
          onRetry={tracksQuery.refetch}
        />
      );
    }

    if (tracksQuery.tracks.length === 0) {
      return (
        <InstagramAudioTrackListEmpty searchQuery={debouncedSearchQuery} />
      );
    }

    return (
      <InstagramAudioTrackList
        tracks={tracksQuery.tracks}
        selectedAudioId={effectiveSelectedId}
        hasNextPage={tracksQuery.hasNextPage}
        isFetchingNextPage={tracksQuery.isFetchingNextPage}
        sentryRef={tracksQuery.sentryRef}
        onToggleSelect={(track) =>
          setPendingTrack(track.audio_id === effectiveSelectedId ? null : track)
        }
      />
    );
  }

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-4 tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-sm"
      data-name="instagram-audio_picker-card"
    >
      <span className="tw-text-md tw-font-semibold tw-text-grayscale-180">
        {t('instagramAudio.pickerTitle')}
      </span>

      <div className="tw-flex tw-flex-col tw-gap-3">
        <InstagramAudioSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
        />
        <InstagramAudioTypeTabs value={audioType} onChange={setAudioType} />
      </div>

      <div
        ref={tracksQuery.rootRef}
        className="tw-h-[min(28rem,50dvh,calc(100dvh-30rem))] tw-min-h-[10rem] tw-overflow-y-auto"
      >
        {renderTracks()}
      </div>

      <div className="tw-sticky tw-bottom-0 tw-z-[1] tw-flex tw-justify-end tw-gap-3 tw-border-t tw-border-grayscale-10 tw-bg-white tw-pt-4">
        <Button
          variant="secondary"
          onClick={onBack}
          data-cy="instagram-audio-picker_cancel-button"
        >
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          disabled={pendingTrack === undefined}
          onClick={handleConfirm}
          data-cy="instagram-audio-picker_submit-button"
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
};

export default InstagramAudioPickerCard;
